// Ebook offert — Nid de Lune (15 oct. 2026)
// Tient le panier à jour : 1 ebook cadeau dès qu'un produit éligible est présent, aucun sinon.
// La ligne cadeau porte la propriété cachée « _cadeau » ; un ebook acheté volontairement n'est jamais retiré.
import { CartLinesUpdateEvent } from '@shopify/events';

const config = window.tmEbookCadeau;
const MARQUE = '_cadeau';
const ROUTES_PANIER = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
let enCours = false;
let aRefaire = false;

const estEbook = (ligne) => ligne.product_id === config.productId;
const estCadeau = (ligne) => estEbook(ligne) && ligne.properties && ligne.properties[MARQUE];
const estEligible = (ligne) => !estEbook(ligne) && !ligne.gift_card && ligne.product_type !== 'PLANET_CONTRIBUTION';

async function lirePanier() {
  const reponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
  return reponse.json();
}

async function envoyer(route, corps) {
  const reponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/${route}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Tm-Ebook': '1' },
    body: JSON.stringify(corps),
  });
  if (!reponse.ok) throw new Error(`tm-ebook-cadeau : ${route} a échoué (${reponse.status})`);
}

// Prévient le thème (compteur, tiroir, page panier) que le panier a changé
function annoncer(action, panier) {
  const promesse = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ merchandiseId: String(config.variantId), quantity: action === 'add' ? 1 : 0 }],
      promise: promesse.promise,
    })
  );
  promesse.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
    detail: { items: panier.items, itemCount: panier.item_count, source: 'tm-ebook-cadeau', sourceId: 'tm-ebook-cadeau' },
  });
  if (document.body.matches('.template-cart, [data-template="cart"]') || location.pathname.replace(/\/$/, '').endsWith('/cart')) {
    location.reload();
  }
}

// --- Affichage dans le panier CornerCart (tiroir et page panier) --------------------------
// CornerCart numérote ses lignes dans l'ordre de /cart.js. Deux retouches :
//  1. « Gratuit » → « 0.00 EUR » sur l'ebook offert (même format que les autres prix CornerCart) ;
//  2. l'étiquette de remise n'est laissée que sur les lignes réellement remisées (la remise « Achetez X »
//     attache une remise de 0 € aux produits achetés, que CornerCart affichait aussi).
let dernierPanier = null;
const montantRemise = (ligne) => (ligne.line_level_discount_allocations || []).reduce((s, a) => s + a.amount, 0);
function retoucherCornerCart() {
  if (!dernierPanier) return;
  dernierPanier.items.forEach((ligne, i) => {
    // Tiroir et page panier Horizon : lignes <tr id="CartItem-<variante>:<clé>">, remises en <li><bdi>titre</bdi></li>
    const titres = (ligne.line_level_discount_allocations || []).filter((a) => a.amount === 0).map((a) => a.discount_application.title);
    if (titres.length) {
      document.querySelectorAll(`tr[id$=":${ligne.key.split(':').pop()}"] li`).forEach((li) => {
        const texte = li.querySelector('bdi')?.textContent.trim();
        if (texte && titres.includes(texte) && li.style.display !== 'none') li.style.display = 'none';
      });
    }
    const etiquettes = document.getElementById(`corner-cowi-cart-item-discounts-${i}`);
    if (etiquettes) {
      // CornerCart pose « display: flex !important » : il faut une valeur prioritaire, sur le conteneur de l'étiquette
      const bloc = etiquettes.closest('corner-cart-line-discounts') || etiquettes;
      const masquer = montantRemise(ligne) === 0;
      if (masquer && bloc.style.getPropertyValue('display') !== 'none') bloc.style.setProperty('display', 'none', 'important');
      if (!masquer && bloc.style.getPropertyValue('display') === 'none') bloc.style.removeProperty('display');
    }
    if (estEbook(ligne) && ligne.final_line_price === 0) {
      const prix = document.getElementById(`corner-cowi-cart-item-price-${i}`);
      const gratuit = prix && [...prix.querySelectorAll('p, span')].find((e) => !e.children.length && /^\s*(Gratuit|Free)\s*$/i.test(e.textContent));
      if (gratuit) gratuit.textContent = `0.00 ${dernierPanier.currency}`;
    }
  });
}
let retoucheDemandee = false;
new MutationObserver(() => {
  if (retoucheDemandee) return;
  retoucheDemandee = true;
  requestAnimationFrame(() => { retoucheDemandee = false; retoucherCornerCart(); });
}).observe(document.documentElement, { childList: true, subtree: true });

async function equilibrer() {
  if (enCours) { aRefaire = true; return; }
  enCours = true;
  try {
    let panier = await lirePanier();
    dernierPanier = panier;
    const eligibles = panier.items.filter(estEligible).length;
    const cadeaux = panier.items.filter(estCadeau);
    const ebookPresent = panier.items.some(estEbook);
    // Un ebook ajouté par le client suffit : c'est lui qui reçoit la remise, la ligne cadeau ferait doublon (et serait facturée)
    const ebookDuClient = panier.items.some((l) => estEbook(l) && !estCadeau(l));

    if (eligibles > 0 && !ebookPresent) {
      await envoyer('add', { items: [{ id: config.variantId, quantity: 1, properties: { [MARQUE]: '1' } }] });
      annoncer('add', await lirePanier());
    } else if (cadeaux.length && (eligibles === 0 || ebookDuClient)) {
      await envoyer('update', { updates: Object.fromEntries(cadeaux.map((l) => [l.key, 0])) });
      annoncer('remove', await lirePanier());
    } else if (cadeaux.some((l) => l.quantity > 1)) {
      await envoyer('update', { updates: Object.fromEntries(cadeaux.map((l) => [l.key, 1])) });
      annoncer('update', await lirePanier());
    }
    dernierPanier = await lirePanier();
    retoucherCornerCart();
  } catch (erreur) {
    console.warn(erreur);
  } finally {
    enCours = false;
    if (aRefaire) { aRefaire = false; equilibrer(); }
  }
}

// Observe les changements de panier faits par le thème ou par une application (fetch et XHR)
const fetchOrigine = window.fetch;
window.fetch = function (ressource, options) {
  const promesse = fetchOrigine.apply(this, arguments);
  const url = typeof ressource === 'string' ? ressource : ressource?.url || '';
  const deNous = options?.headers?.['X-Tm-Ebook'];
  if (!deNous && ROUTES_PANIER.test(url)) promesse.then(() => setTimeout(equilibrer, 50)).catch(() => {});
  return promesse;
};
const ouvrirOrigine = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (methode, url) {
  if (ROUTES_PANIER.test(String(url))) this.addEventListener('loadend', () => setTimeout(equilibrer, 50));
  return ouvrirOrigine.apply(this, arguments);
};

equilibrer();
window.addEventListener('pageshow', (e) => { if (e.persisted) equilibrer(); });
